/**
 * 插件沙箱 iframe：加载插件 HTML 资源，在 `load` 后将 iframe 注册到 `PluginManager` 并发送 `init` 载荷，
 * 建立宿主与插件之间的 postMessage Bridge；可选监听 `ui.sizeTo` 以支持弹层内高度自适应。
 */
import type { InitPayload } from "@aspect/plugin-sdk";
import { useCallback, useEffect, useRef, useState } from "react";

import { usePluginManager } from "./plugin-manager-context";

interface PluginIframeProps {
  pluginId: string;
  src: string;
  className?: string;
  style?: React.CSSProperties;
  /** 合并进 `sendInitToIframe` 的额外字段（如 viewer 传入当前条目 `item`） */
  extraInitPayload?: Partial<InitPayload>;
  /** 插件 RPC `ui.sizeTo` 回调高度时，用于更新外层容器高度 */
  onSizeTo?: (height: number) => void;
}

/** 允许脚本、同源、表单与弹窗；与插件 UI 能力对齐的最小 sandbox 集合 */
const SANDBOX = "allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox";

function primeIframeTheme(iframe: HTMLIFrameElement) {
  const iframeDocument = iframe.contentDocument;
  if (!iframeDocument) {
    return;
  }

  const hostRoot = document.documentElement;
  const hostStyles = getComputedStyle(hostRoot);
  const resolvedTheme = hostRoot.classList.contains("dark") ? "dark" : "light";
  const iframeRoot = iframeDocument.documentElement;
  const iframeBody = iframeDocument.body;

  iframeRoot.classList.remove("light", "dark");
  iframeRoot.classList.add(resolvedTheme);
  iframeRoot.style.colorScheme = resolvedTheme;
  iframeRoot.style.setProperty("--background", hostStyles.getPropertyValue("--background").trim());
  iframeRoot.style.setProperty("--foreground", hostStyles.getPropertyValue("--foreground").trim());

  if (iframeBody) {
    iframeBody.style.background = "var(--background)";
    iframeBody.style.color = "var(--foreground)";
  }
}

export function PluginIframe({
  pluginId,
  src,
  className,
  style,
  extraInitPayload,
  onSizeTo,
}: PluginIframeProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const manager = usePluginManager();
  const registeredRef = useRef(false);
  const [isReady, setIsReady] = useState(false);

  const handleLoad = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe || !manager) return;

    primeIframeTheme(iframe);

    if (!registeredRef.current) {
      registeredRef.current = true;
      manager.registerIframe(pluginId, iframe, "*");
      manager.sendInitToIframe(pluginId, extraInitPayload);
    }

    requestAnimationFrame(() => {
      setIsReady(true);
    });
  }, [pluginId, manager, extraInitPayload]);

  useEffect(() => {
    if (!onSizeTo) return;

    const handler = (e: MessageEvent) => {
      if (e.source !== iframeRef.current?.contentWindow) return;
      const data = e.data;
      if (data?.type === "rpc-request" && data?.method === "ui.sizeTo") {
        const height = data.params?.height;
        if (typeof height === "number") {
          onSizeTo(height);
        }
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [onSizeTo]);

  useEffect(() => {
    return () => {
      registeredRef.current = false;
      setIsReady(false);
    };
  }, []);

  return (
    <iframe
      ref={iframeRef}
      src={src}
      sandbox={SANDBOX}
      className={className}
      style={{
        border: "none",
        width: "100%",
        height: "100%",
        background: "var(--background, #111827)",
        colorScheme: document.documentElement.classList.contains("dark") ? "dark" : "light",
        visibility: isReady ? "visible" : "hidden",
        ...style,
      }}
      onLoad={handleLoad}
    />
  );
}
